import { Component, OnInit, ViewChild } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { MemoryService } from './services/memory.service';
import { MobileViewService } from './services/mobile-view.service';
import { MatIcon } from '@angular/material/icon';
import { MatAnchor } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NavigationComponent } from './components/navigation/navigation.component';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { HeaderComponent } from './components/header/header.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { JwtService } from './services/jwt.service';
import { ApiResponses, BackendService } from './services/backend.service';
import { NotificationService } from './services/notification.service';
import { TranslationBackendService } from './services/translation-file.service';
import { capabilityCheck } from './pipes/check-capability.pipe';
import { RouteData } from './interfaces/route';
import { HrSessionService } from './services/hr-session.service';
import { ServerUnreachableOverlayComponent } from './components/server-unreachable-overlay/server-unreachable-overlay.component';


const DEFAULT_WIDTH_CLASS = 'default-width';

type StaticPage = ApiResponses['static/getAllPages'][0];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    TranslocoDirective,
    HeaderComponent,
    MatSidenavContainer,
    MatSidenav,
    NavigationComponent,
    MatSidenavContent,
    MatProgressBar,
    RouterOutlet,
    MatAnchor,
    MatIcon,
    NgClass,
    AsyncPipe,
    ServerUnreachableOverlayComponent
  ]
})
export class AppComponent implements OnInit {
  @ViewChild('sideNav') sideNav!: MatSidenav;

  showDrawer = true;
  title = environment.appTitle;
  navigating = false;
  mainLayoutClass: string = DEFAULT_WIDTH_CLASS;

  awaitingLogin = false;

  staticPageMenu: { [s: string]: StaticPage[] } = {};

  showReload: boolean;
  reloadTime = 5;

  isMobileView: boolean;
  isOpened: boolean;
  isLoggedIn: boolean;
  intervalId: NodeJS.Timeout;
  showHeader = true;
  /** True while a route marked `bare` is active — header and navigation stay hidden. */
  bareRoute = false;
  readonly serverUnreachable$;

  constructor(
    private readonly jwtService: JwtService,
    private readonly backendService: BackendService,
    private memoryService: MemoryService,
    private router: Router,
    private readonly notificationService: NotificationService,
    private readonly mobileViewService: MobileViewService,
    private readonly translationBackendService: TranslationBackendService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly _hrSessionService: HrSessionService
  ) {
    this.serverUnreachable$ = this.notificationService.serverUnreachable$;
    this.isMobileView = mobileViewService.detectMobileDevice();

    this.isOpened = this.isMobileView ? false : true;
    this.translationBackendService.getAllFETranslations();

    if (this.jwtService.isLoggedIn) {
      this.isLoggedIn = true;
      this.onLoggedIn();
    }
    this.jwtService.$loginChange.subscribe((result) => {
      if (!result.userdata) {
        this.isLoggedIn = false;
        return;
      }
      this.isLoggedIn = true;
      this.onLoggedIn();
    });

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.navigating = true;
      }
      if (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) {
        this.navigating = false;
        this.updateMainLayoutClass();
      }
    });

    this.memoryService.$changeHeaderVisibility.subscribe((e) => {
      this.showHeader = e;
    });
  }

  ngOnInit(): void {
    this.isMobileView = this.mobileViewService.detectMobileDevice();
    this.updateMainLayoutClass();

    this.notificationService.showReloadButton$.subscribe((reload) => {
      this.showReload = reload;
      this.intervalId = setInterval(() => {
        if (this.reloadTime > 0) {
          this.reloadTime--;
        } else {
          clearInterval(this.intervalId);
        }
      }, 1000);
      setTimeout(() => {
        this.reload();
      }, 5000);
    });
    document.title = this.title;
    document.body.style.setProperty('background-image', `url(${environment.apiUrl}static/background)`);
  }

  onLoggedIn() {
    this.awaitingLogin = false;
    if (!this.jwtService.role?.capabilities.length || [5, 9, 13, 20].includes(this.jwtService.role?.id)) {
      this.showDrawer = false;
    } else {
      this.showDrawer = true;

      if (capabilityCheck(this.jwtService.role?.capabilities, 'emergency.view')) {
        this.backendService.get<'static/getAllPages'>('static/page/all').subscribe({
          next: (p) => {
            this.buildMenuFromStaticPages(p);
          },
          error: (error) => {
            console.log('Failed to load static pages, continuing without them:', error);
            // Don't prevent app from working if static pages fail to load
          }
        });
      }
    }
  }

  toggleDrawer() {
    this.isOpened = !this.isOpened;
  }

  onMenuItemSelected() {
    if (this.isMobileView) this.isOpened = false;
  }

  reload() {
    this.showReload = false;
    window.location.reload();
  }

  navBarToggle(): void {
    this.isOpened = !this.isOpened;
  }

  private buildMenuFromStaticPages(staticPages: StaticPage[]) {
    const menu: { [s: string]: StaticPage[] } = {};
    for (const page of staticPages) {
      menu[page.menu] = menu[page.menu] || [];
      menu[page.menu].push(page);
    }
    this.staticPageMenu = menu;
  }

  /** Sets an additional CSS class on the main layout container if specified in the route data. This is useful e.g. to change between full-width and contained layouts. */
  private updateMainLayoutClass(): void {
    const deepestRoute = this.getDeepestPrimaryRoute(this.activatedRoute);
    const routeData = deepestRoute.snapshot.data as RouteData;
    const layoutClassData = routeData.layoutClass;

    // A bare route renders as a standalone document. Only the flag is kept — the template gates
    // header and navigation on it, so nothing has to be restored when the route is left again.
    this.bareRoute = routeData.bare === true;

    if (layoutClassData !== undefined) {
      this.mainLayoutClass = layoutClassData || '';
      return;
    }

    if (typeof layoutClassData === 'string') {
      const trimmed = layoutClassData.trim();
      this.mainLayoutClass = trimmed.length ? trimmed : DEFAULT_WIDTH_CLASS;
      return;
    }

    this.mainLayoutClass = DEFAULT_WIDTH_CLASS;
  }

  private getDeepestPrimaryRoute(route: ActivatedRoute): ActivatedRoute {
    let currentRoute = route;
    let primaryChild = currentRoute.children.find((child) => child.outlet === 'primary');

    while (primaryChild) {
      currentRoute = primaryChild;
      primaryChild = currentRoute.children.find((child) => child.outlet === 'primary');
    }

    return currentRoute;
  }
}
